import { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Search, X, MapPin } from "lucide-react";
import { Button } from "@tabipla/ui/components/button";
import { Checkbox } from "@tabipla/ui/components/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@tabipla/ui/components/dialog";
import { Input } from "@tabipla/ui/components/input";
import { toast } from "sonner";
import { useSpots } from "../../../application/hooks/useSpots";
import { useAddSpotToBooklet } from "../../../application/hooks/useBooklets";
import { getCategoryById } from "../../../config/categories";
import { cn } from "@tabipla/ui/lib/utils";
import { PlaceAutocomplete, type PlaceResult } from "../../components/PlaceAutocomplete";

interface AddSpotsDialogProps {
  bookletId: string;
  existingSpotIds: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddSpotsDialog({
  bookletId,
  existingSpotIds,
  open,
  onOpenChange,
}: AddSpotsDialogProps) {
  const { t } = useTranslation();
  const { data: allSpots, isLoading } = useSpots();
  const addSpotMutation = useAddSpotToBooklet(bookletId);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [isAdding, setIsAdding] = useState(false);

  const handleLocationChange = useCallback((place: PlaceResult | null) => {
    if (place) {
      // Use main text (e.g., "京都市", "東京都") for filtering
      setLocationFilter(place.name || place.formattedAddress);
    } else {
      setLocationFilter("");
    }
  }, []);

  // Filter out already added spots and apply search + location filter
  const availableSpots = useMemo(() => {
    if (!allSpots) return [];
    let filtered = allSpots.filter((spot) => !existingSpotIds.includes(spot.id));

    // Apply location filter first
    if (locationFilter.trim()) {
      const location = locationFilter.toLowerCase().trim();
      filtered = filtered.filter((spot) => {
        if (spot.address?.toLowerCase().includes(location)) return true;
        return false;
      });
    }

    // Then apply text search
    if (!searchQuery.trim()) return filtered;

    const query = searchQuery.toLowerCase().trim();
    return filtered.filter((spot) => {
      if (spot.name.toLowerCase().includes(query)) return true;
      if (spot.description?.toLowerCase().includes(query)) return true;
      if (spot.address?.toLowerCase().includes(query)) return true;
      return false;
    });
  }, [allSpots, existingSpotIds, searchQuery, locationFilter]);

  const handleToggleSpot = (spotId: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(spotId)) {
        newSet.delete(spotId);
      } else {
        newSet.add(spotId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === availableSpots.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(availableSpots.map((s) => s.id)));
    }
  };

  const handleAdd = async () => {
    if (selectedIds.size === 0) return;

    setIsAdding(true);
    try {
      // Add spots sequentially
      for (const spotId of selectedIds) {
        await addSpotMutation.mutateAsync({ spotId });
      }
      toast.success(t("booklets.spotsAdded", { count: selectedIds.size }));
      setSelectedIds(new Set());
      setSearchQuery("");
      onOpenChange(false);
    } catch {
      toast.error(t("common.error"));
    } finally {
      setIsAdding(false);
    }
  };

  const handleClose = () => {
    setSelectedIds(new Set());
    setSearchQuery("");
    setLocationFilter("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[80vh] overflow-hidden sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("booklets.addSpots")}</DialogTitle>
        </DialogHeader>

        {/* Location Filter */}
        <div className="space-y-1">
          <label className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
            <MapPin className="h-3 w-3" />
            {t("booklets.locationFilter", "地域で絞り込み")}
          </label>
          <PlaceAutocomplete
            value={locationFilter}
            onChange={handleLocationChange}
            placeholder={t("booklets.locationFilterPlaceholder", "例: 京都、東京...")}
          />
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder={t("spots.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-9 pl-9 pr-9"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Select All */}
        {availableSpots.length > 0 && (
          <div className="flex items-center justify-between border-b pb-2">
            <button
              onClick={handleSelectAll}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            >
              <Checkbox
                checked={selectedIds.size === availableSpots.length && availableSpots.length > 0}
                className="h-5 w-5"
              />
              {t("booklets.selectAll")}
            </button>
            {selectedIds.size > 0 && (
              <span className="text-sm text-muted-foreground">
                {t("booklets.selectedCount", { count: selectedIds.size })}
              </span>
            )}
          </div>
        )}

        {/* Spot List */}
        <div className="max-h-[40vh] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : availableSpots.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {searchQuery
                ? t("spots.noSearchResults", { query: searchQuery })
                : t("booklets.noAvailableSpots")}
            </div>
          ) : (
            <div className="space-y-2">
              {availableSpots.map((spot) => {
                const category = getCategoryById(spot.category);
                const CategoryIcon = category.icon;
                const isSelected = selectedIds.has(spot.id);

                return (
                  <button
                    key={spot.id}
                    onClick={() => handleToggleSpot(spot.id)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors",
                      isSelected ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"
                    )}
                  >
                    <Checkbox checked={isSelected} className="h-5 w-5" />

                    {spot.images && spot.images.length > 0 ? (
                      <div className="h-10 w-10 shrink-0 overflow-hidden rounded">
                        <img
                          src={spot.images[0].url}
                          alt={spot.name}
                          className="h-full w-full object-cover"
                        />
                      </div>
                    ) : (
                      <div
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded"
                        style={{ backgroundColor: `${category.color}15` }}
                      >
                        <CategoryIcon className="h-5 w-5" style={{ color: category.color }} />
                      </div>
                    )}

                    <div className="min-w-0 flex-1">
                      <h3 className="truncate font-medium">{spot.name}</h3>
                      {spot.address && (
                        <p className="truncate text-xs text-muted-foreground">{spot.address}</p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleAdd} disabled={selectedIds.size === 0 || isAdding}>
            {isAdding ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {t("booklets.addSelectedSpots", { count: selectedIds.size })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
